import {
  listTreeCorrectnessSimulationEvidence,
  listTreeCorrectnessSimulationRuns,
  listTreeScaleSimulationResults,
  runAllTreeCorrectnessScenarios,
  runTreeCorrectnessScenario,
  runTreeRuntimeSimulator,
  runTreeScaleSimulation,
} from '@/governor/tree-runtime-simulator';
import {
  TREE_SCALE_SIMULATION_PROFILES,
} from '@/governor/tree-runtime-simulator/TreeScaleSimulationProfiles';

import {
  useMemo,
  useState,
} from 'react';

import type {
  TreeRuntimeScenarioId,
  TreeScaleProfileId,
} from '@/governor/tree-runtime-simulator';

export default function TreeSimulationWorkspace({
  onSimulationChange,
}: {
  onSimulationChange?: () => void;
}) {
  const correctness =
    runTreeRuntimeSimulator();

  const [
    runningScenarioId,
    setRunningScenarioId,
  ] = useState<TreeRuntimeScenarioId | 'all' | null>(
    null,
  );

  const [
    revision,
    setRevision,
  ] = useState(0);

  const [
    runningScaleProfileId,
    setRunningScaleProfileId,
  ] = useState<TreeScaleProfileId | null>(
    null,
  );

  const runs = useMemo(
    () =>
      listTreeCorrectnessSimulationRuns(),
    [revision],
  );

  const evidence = useMemo(
    () =>
      listTreeCorrectnessSimulationEvidence(),
    [revision],
  );

  const scaleResults = useMemo(
    () =>
      listTreeScaleSimulationResults(),
    [revision],
  );

  const latestScaleResultByProfile =
    useMemo(() => {
      const map = new Map<string, typeof scaleResults[number]>();

      for (const result of scaleResults) {
        if (!map.has(result.profileId)) {
          map.set(result.profileId, result);
        }
      }

      return map;
    }, [scaleResults]);

  const latestRunByScenario = useMemo(() => {
    const map = new Map(
      [] as Array<
        [
          string,
          ReturnType<
            typeof listTreeCorrectnessSimulationRuns
          >[number],
        ]
      >,
    );

    for (const run of runs) {
      if (!map.has(run.scenarioId)) {
        map.set(
          run.scenarioId,
          run,
        );
      }
    }

    return map;
  }, [runs]);

  const runScenario = async (
    scenarioId: TreeRuntimeScenarioId,
  ) => {
    setRunningScenarioId(scenarioId);

    try {
      await runTreeCorrectnessScenario(
        scenarioId,
      );
    } finally {
      setRunningScenarioId(null);
      setRevision(value => value + 1);
      onSimulationChange?.();
    }
  };

  const runAll = async () => {
    setRunningScenarioId('all');

    try {
      await runAllTreeCorrectnessScenarios();
    } finally {
      setRunningScenarioId(null);
      setRevision(value => value + 1);
      onSimulationChange?.();
    }
  };

  const runScaleProfile = async (
    profileId: TreeScaleProfileId,
  ) => {
    setRunningScaleProfileId(profileId);

    try {
      await runTreeScaleSimulation(profileId);
    } finally {
      setRunningScaleProfileId(null);
      setRevision(value => value + 1);
      onSimulationChange?.();
    }
  };

  return (
    <details className="mb-5 rounded-[2rem] border border-sky-200 bg-sky-50 p-5">
      <summary className="cursor-pointer text-xl font-bold text-slate-950">
        Tree Simulator
        <span className="ml-3 rounded-full border border-sky-300 bg-white px-3 py-1 text-xs uppercase text-sky-800">
          Simulated
        </span>
      </summary>

      <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        This simulator is isolated from production family records.
        Production writes, repair execution, and live certification
        are blocked.
      </div>

      <section className="mt-5">
        <h3 className="text-xl font-bold text-slate-950">
          Relationship correctness scenarios
        </h3>

        <p className="mt-2 text-sm text-slate-600">
          Registered catalog version {correctness.version}.
          Scenarios execute through the controlled in-memory
          simulator.
        </p>

        <button
          type="button"
          disabled={runningScenarioId !== null}
          onClick={() => {
            void runAll();
          }}
          className="mt-4 w-full rounded-2xl border border-sky-300 bg-white px-4 py-3 font-bold text-sky-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {runningScenarioId === 'all'
            ? 'Running all Tree scenarios...'
            : 'Run all Tree scenarios'}
        </button>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <p>
            Recorded runs: <b>{runs.length}</b>
          </p>
          <p>
            Simulation evidence records:{' '}
            <b>{evidence.length}</b>
          </p>
          <p>
            Production mutations: <b>0 allowed</b>
          </p>
        </div>

        <div className="mt-4 space-y-3">
          {correctness.scenarios.map(scenario => {
            const latestRun =
              latestRunByScenario.get(
                scenario.id,
              );

            return (
              <div
                key={scenario.id}
                className="rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-slate-950">
                    {scenario.id.replaceAll('_', ' ')}
                  </p>

                  <span
                    className={
                      'rounded-full border px-3 py-1 text-xs font-bold uppercase ' +
                      (
                        latestRun?.passed
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : latestRun
                            ? 'border-rose-200 bg-rose-50 text-rose-800'
                            : 'border-slate-200 bg-slate-50 text-slate-600'
                      )
                    }
                  >
                    {latestRun
                      ? latestRun.state
                      : 'Not run'}
                  </span>
                </div>

                <p className="mt-2 text-sm text-slate-700">
                  {scenario.purpose}
                </p>

                <p className="mt-2 text-xs text-slate-500">
                  Expected evidence:{' '}
                  {scenario.expectedEvidence.join(', ')}
                </p>

                {latestRun && (
                  <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                    <p>
                      Passed:{' '}
                      <b>
                        {latestRun.passed
                          ? 'Yes'
                          : 'No'}
                      </b>
                    </p>

                    <p>
                      Cleanup completed:{' '}
                      <b>
                        {latestRun.cleanupCompleted
                          ? 'Yes'
                          : 'No'}
                      </b>
                    </p>

                    <p>
                      Production mutation:{' '}
                      <b>
                        {latestRun.productionMutationOccurred
                          ? 'Detected'
                          : 'None'}
                      </b>
                    </p>

                    <p>
                      Evidence records:{' '}
                      <b>
                        {latestRun.evidenceIds.length}
                      </b>
                    </p>

                    {latestRun.error && (
                      <p className="mt-2 text-rose-700">
                        {latestRun.error}
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  disabled={runningScenarioId !== null}
                  onClick={() => {
                    void runScenario(
                      scenario.id,
                    );
                  }}
                  className="mt-3 w-full rounded-xl border border-violet-300 bg-white px-4 py-2 text-sm font-bold text-violet-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {runningScenarioId === scenario.id
                    ? 'Running scenario...'
                    : 'Run scenario'}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-6">
        <h3 className="text-xl font-bold text-slate-950">
          Scale and stress profiles
        </h3>

        <p className="mt-2 text-sm text-slate-600">
          Large targets use bounded measured samples and explicit
          capacity projections. They are not live certification.
        </p>

        <div className="mt-4 space-y-3">
          {TREE_SCALE_SIMULATION_PROFILES.map(profile => (
            <div
              key={profile.id}
              className="rounded-2xl border border-slate-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-bold text-slate-950">
                  {profile.label}
                </p>

                <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-bold uppercase text-violet-800">
                  {profile.projectionRequired
                    ? 'Projected'
                    : 'Measured target'}
                </span>
              </div>

              <p className="mt-2 text-sm text-slate-700">
                {profile.purpose}
              </p>

              <p className="mt-3 text-sm text-slate-700">
                Target users:{' '}
                <b>
                  {profile.targetUserCount.toLocaleString()}
                </b>
              </p>

              <p className="text-sm text-slate-700">
                Maximum measured sample:{' '}
                <b>
                  {profile.measuredSampleSize.toLocaleString()}
                </b>
              </p>

              {latestScaleResultByProfile.get(profile.id) && (
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                  <p>
                    Result:{' '}
                    <b>
                      {latestScaleResultByProfile.get(profile.id)?.state}
                    </b>
                  </p>
                  <p>
                    Measured time:{' '}
                    <b>
                      {latestScaleResultByProfile.get(profile.id)
                        ?.phaseDurations.totalMs} ms
                    </b>
                  </p>
                  <p>
                    Throughput:{' '}
                    <b>
                      {latestScaleResultByProfile.get(profile.id)
                        ?.measuredThroughputPerSecond.toLocaleString()}
                      {' '}members/sec
                    </b>
                  </p>
                  <p>
                    Projected target time:{' '}
                    <b>
                      {latestScaleResultByProfile.get(profile.id)
                        ?.projectedTotalDurationMs.toLocaleString()}
                      {' '}ms
                    </b>
                  </p>
                  <p>
                    Cleanup completed:{' '}
                    <b>
                      {latestScaleResultByProfile.get(profile.id)
                        ?.cleanupCompleted
                        ? 'Yes'
                        : 'No'}
                    </b>
                  </p>
                  <p>
                    Production mutation: <b>None</b>
                  </p>
                </div>
              )}

              <p className="mt-3 text-xs font-bold uppercase text-rose-700">
                Live certified: No
              </p>

              <button
                type="button"
                disabled={
                  runningScaleProfileId !== null ||
                  runningScenarioId !== null
                }
                onClick={() => {
                  void runScaleProfile(profile.id);
                }}
                className="mt-3 w-full rounded-xl border border-sky-300 bg-white px-4 py-2 text-sm font-bold text-sky-900 disabled:opacity-60"
              >
                {runningScaleProfileId === profile.id
                  ? 'Running bounded stress test...'
                  : profile.projectionRequired
                    ? 'Run measured sample and projection'
                    : 'Run measured stress test'}
              </button>
            </div>
          ))}
        </div>
      </section>
    </details>
  );
}
